import * as React from 'react';
import {
  ColumnDef,
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  Row,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';
import { cn } from '../../lib/utils';

// Le tableau de données commun (2026-09-23).
//
// Chaque page réécrivait son tri, sa sélection, son état vide, son chargement
// et sa version mobile. Ici ils existent une fois : une page ne décrit plus
// que ses colonnes. Le filtrage reste à la page — elle sait ce qu'il veut dire
// (catégorie, statut de paiement…) — et passe des données déjà filtrées.

export interface DataTableProps<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
  getRowId: (row: T) => string;
  loading?: boolean;
  /** Sélection multiple : case en tête de ligne et « tout sélectionner ». */
  selectable?: boolean;
  rowSelection?: RowSelectionState;
  /** Un `setState` : reçoit la valeur OU la fonction de mise à jour, pour que
   *  deux clics avant le rendu suivant ne s'écrasent pas. */
  onRowSelectionChange?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  /** Ligne cliquable sur toute sa largeur (ouvre le détail). */
  onRowClick?: (row: T) => void;
  /** Ligne mise en avant (celle qu'on édite dans le panneau latéral). */
  activeRowId?: string | null;
  rowClassName?: (row: T) => string | undefined;
  rowDomId?: (row: T) => string;
  initialSorting?: SortingState;
  empty?: React.ReactNode;
  /** Rendu mobile : sous `md`, une carte par ligne au lieu d'un tableau. */
  renderCard?: (row: T, ctx: { selected: boolean; toggle: () => void }) => React.ReactNode;
  className?: string;
  ariaLabel?: string;
}

export function DataTable<T>({
  columns,
  data,
  getRowId,
  loading,
  selectable,
  rowSelection,
  onRowSelectionChange,
  onRowClick,
  activeRowId,
  rowClassName,
  rowDomId,
  initialSorting = [],
  empty,
  renderCard,
  className,
  ariaLabel,
}: DataTableProps<T>) {
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting);

  const selectColumn: ColumnDef<T, any> = React.useMemo(() => ({
    id: '__select',
    enableSorting: false,
    size: 36,
    header: ({ table }) => (
      <Checkbox
        aria-label="Tout sélectionner"
        checked={table.getIsAllRowsSelected() ? true : table.getIsSomeRowsSelected() ? 'indeterminate' : false}
        onCheckedChange={(v) => table.toggleAllRowsSelected(v === true)}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        aria-label="Sélectionner la ligne"
        checked={row.getIsSelected()}
        onCheckedChange={(v) => row.toggleSelected(v === true)}
        onClick={(e) => e.stopPropagation()}
      />
    ),
  }), []);

  const table = useReactTable({
    data,
    columns: selectable ? [selectColumn, ...columns] : columns,
    getRowId: (row) => getRowId(row),
    state: { sorting, rowSelection: rowSelection ?? {} },
    enableRowSelection: !!selectable,
    onSortingChange: setSorting,
    onRowSelectionChange: (updater) => onRowSelectionChange?.(updater),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;
  const colCount = table.getVisibleLeafColumns().length;

  const rowProps = (row: Row<T>) => ({
    id: rowDomId?.(row.original),
    'data-state': row.getIsSelected() ? 'selected' : undefined,
    onClick: onRowClick ? () => onRowClick(row.original) : undefined,
    className: cn(
      'border-b border-white/[0.06] transition-colors',
      onRowClick && 'cursor-pointer hover:bg-white/[0.03]',
      row.getIsSelected() && 'bg-white/[0.05]',
      activeRowId === row.id && 'bg-white/[0.07] shadow-[inset_2px_0_0_hsl(var(--foreground))]',
      rowClassName?.(row.original),
    ),
  });

  return (
    <div className={className}>
      {/* Tableau — md et au-delà. Pas de carte autour : une collection se lit
          en lignes ouvertes (charte, « collections ouvertes »). */}
      <div className={cn('overflow-x-auto', renderCard && 'hidden md:block')}>
        <table className="w-full border-collapse text-sm" aria-label={ariaLabel}>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-white/[0.08]">
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const dir = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      style={header.column.id === '__select' ? { width: 36 } : undefined}
                      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined}
                      className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 uppercase tracking-[0.08em] hover:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {dir === 'asc' && <ArrowUp size={12} />}
                          {dir === 'desc' && <ArrowDown size={12} />}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b border-white/[0.06]">
                  {Array.from({ length: colCount }).map((__, j) => (
                    <td key={j} className="px-3 py-4">
                      <Skeleton className={cn('h-4', j === 0 && selectable ? 'w-4' : j % 2 ? 'w-32' : 'w-20')} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="px-3 py-12 text-center text-sm text-muted-foreground">
                  {empty}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} {...rowProps(row)}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn('px-3 py-3 align-middle', cell.column.id === '__select' && 'cursor-pointer')}
                      // Toute la cellule de la case coche la ligne : viser une
                      // case de 16 px ne doit pas ouvrir la fiche par erreur.
                      onClick={cell.column.id === '__select'
                        ? (e) => { e.stopPropagation(); row.toggleSelected(); }
                        : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cartes — sous md. Même données, même sélection, même tri. */}
      {renderCard && (
        <div className="md:hidden flex flex-col">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="py-4 border-b border-white/[0.06] space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
            ))
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">{empty}</div>
          ) : (
            rows.map((row) => (
              <div key={row.id} {...rowProps(row)}>
                {renderCard(row.original, {
                  selected: row.getIsSelected(),
                  toggle: () => row.toggleSelected(),
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
